import { useCallback, useRef } from 'react';

import { InviteSegmentEvents } from '../constants';
import type { StartAction, ValidationSource } from '../RenterInvite.types';
import { useInviteTracking } from './useInviteTracking';

type FormFieldError = {
  message?: string;
  type?: string;
};

type UseInviteFormTrackingOptions = {
  onFlowStarted?: () => void;
};

const buildValidationErrors = (fieldErrors: Record<string, unknown>) =>
  Object.entries(fieldErrors)
    .filter(([, error]) => Boolean(error))
    .map(([fieldName, error]) => {
      const errorObj = error as FormFieldError;
      return {
        field_name: fieldName,
        error_code: errorObj.type ?? 'validation',
        error_message_shown: errorObj.message ?? '',
      };
    });

export const useInviteFormTracking = ({ onFlowStarted }: UseInviteFormTrackingOptions = {}) => {
  const { trackInvite } = useInviteTracking();
  const hasFlowStartedRef = useRef(false);

  const trackFlowStarted = useCallback(
    (startAction: StartAction) => {
      if (hasFlowStartedRef.current) return;
      hasFlowStartedRef.current = true;
      onFlowStarted?.();
      trackInvite(InviteSegmentEvents.FlowStarted, { start_action: startAction });
    },
    [onFlowStarted, trackInvite],
  );

  const trackValidationErrors = useCallback(
    (fieldErrors: Record<string, unknown>, source: ValidationSource) => {
      const errors = buildValidationErrors(fieldErrors);
      if (errors.length === 0) return;

      trackInvite(InviteSegmentEvents.ValidationErrorsShown, {
        validation_source: source,
        error_category: 'form_validation',
        errors,
      });
    },
    [trackInvite],
  );

  return { trackFlowStarted, trackValidationErrors };
};
